import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  ActionsSubject,
  select,
  Store
} from '@ngrx/store';
import {
  combineLatest,
  from,
  Observable,
  Subject
} from 'rxjs';
import {
  filter,
  flatMap,
  map,
  mapTo,
  take,
  takeUntil
} from 'rxjs/operators';

import { PortalOutletName } from '@p1/libs/ui-elements';

import * as authReducer from '../../auth/reducer/auth.reducer';
import * as myWatchlistsActions from '../../my-watchlists/actions/my-watchlists.actions';
import * as myWatchlistsReducer from '../../my-watchlists/reducer';
import * as routerActions from '../../routing/actions/router.actions';
import { WatchlistForm } from '../../watchlists/model/watchlist/watchlist-form';
import { isEmptyWatchlist } from '../../watchlists/model/watchlist/watchlist-with-child-lists.helpers';
import { WatchlistSnapshot } from '../model/watchlist-snapshot';
import { notificationMessages } from '../notification-messages/notification-messages';
import * as watchlistSnapshotsReducer from '../reducer';
import * as rootReducer from '../../app.reducers';
import { SetTitleAction } from '../../routing/actions/router.actions';
import { SeoService } from '../../core/service-seo/seo.service';


/**
 * Shows a watchlist snapshot
 */
@Component({
  selector: 'p1-watchlist-snapshot-detail-page',
  templateUrl: './watchlist-snapshot-detail-page.component.html',
  styleUrls: ['./watchlist-snapshot-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WatchlistSnapshotDetailPageComponent implements OnInit, OnDestroy {

  watchlistSnapshot$: Observable<WatchlistSnapshot>;
  childWatchlistSnapshots$: Observable<WatchlistSnapshot[]>;
  parentWatchlistSnapshot$: Observable<WatchlistSnapshot>;
  userAuthenticated$: Observable<boolean>;
  hasContent$: Observable<boolean>;
  myWatchlistsStoreModuleLoaded$: Observable<boolean>;
  copyWatchlistInProgress$: Observable<boolean>;
  showCopyWatchlistDialog: boolean = false;
  portalOutletName = PortalOutletName;
  currentUrl$: Observable<string>;

  private _unsubscribe: Subject<void> = new Subject<void>();


  constructor(
    private _store: Store<watchlistSnapshotsReducer.State>,
    private _actionsSubject: ActionsSubject,
    private _seoService: SeoService
  ) {
    this.currentUrl$ = this._store.pipe(
      select(rootReducer.getRouterUrl)
    );

    this.watchlistSnapshot$ = this._store.pipe(
      select(watchlistSnapshotsReducer.getSelectedItem)
    );

    this.childWatchlistSnapshots$ = this._store.pipe(
      select(watchlistSnapshotsReducer.getSelectedItemChildLists)
    );

    this.parentWatchlistSnapshot$ = this._store.pipe(
      select(watchlistSnapshotsReducer.getSelectedItemParentList)
    );

    this.userAuthenticated$ = this._store.pipe(
      select(authReducer.getIsAuthenticated)
    );

    this.hasContent$ = combineLatest([this.watchlistSnapshot$, this.childWatchlistSnapshots$]).pipe(
      map(([watchlistSnapshot, childWatchlistSnapshots]) => !isEmptyWatchlist(watchlistSnapshot) || childWatchlistSnapshots?.length > 0)
    );
  }

  /**
   * Subscribes to relevant store values
   */
  ngOnInit() {
    this.watchlistSnapshot$.pipe(
      takeUntil(this._unsubscribe)
    ).subscribe(watchlistSnapshot => {
      if (watchlistSnapshot) {
        this._store.dispatch(new SetTitleAction(watchlistSnapshot.name));
        this._seoService.setMetaDescription(watchlistSnapshot.comment?.length > 0 ? watchlistSnapshot.comment : $localize`:|meta description watchlist snapshot detail page @@watchlist-snapshot-detail-page.component_meta-description:Discover the products of the ${watchlistSnapshot.name} watchlist. Open watchlist, import and start planning.`);
        this._seoService.setOgImage('https://downloads.plan.one/open-graph/poster-watchlist.jpg');
      }
    });

    this.myWatchlistsStoreModuleLoaded$ = this.userAuthenticated$.pipe(
      filter(userAuthenticated => userAuthenticated),
      flatMap(() => from(import('src/app/my-watchlists/my-watchlists.store.module').then(m => m.MyWatchlistsStoreModule))),
      mapTo(true)
    );

    this.copyWatchlistInProgress$ = this.myWatchlistsStoreModuleLoaded$.pipe(
      flatMap(() => this._store.pipe(
        select(myWatchlistsReducer.getCreateMyWatchlistInProgress)
      ))
    );
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();

    this._seoService.setMetaDescription(null);
    this._seoService.setOgImage(null);
  }

  openCopyWatchlistDialog() {
    this.showCopyWatchlistDialog = true;
  }

  closeCopyWatchlistDialog() {
    this.showCopyWatchlistDialog = false;
  }

  /**
   * Copies watchlist snapshot by creating a new personal watchlist based on the snapshot
   *
   * @param watchlistFormData
   */
  copyWatchlist(watchlistFormData: WatchlistForm) {
    const newWatchlistName = watchlistFormData.name.length > 60 ? watchlistFormData.name.slice(0, 60) + '...' : watchlistFormData.name;
    this._store.dispatch(new myWatchlistsActions.CreateMyWatchlistAction({
      watchlistFormData,
      succeededMessage: notificationMessages.copyWatchlistSucceeded.replace('$WATCHLIST_NAME$', newWatchlistName),
      failedMessage: notificationMessages.copyWatchlistFailed.replace('$WATCHLIST_NAME$', newWatchlistName)
    }));

    this._actionsSubject.pipe(
      filter(action =>
        action.type === myWatchlistsActions.CREATE_MY_WATCHLIST_SUCCEEDED
        || action.type === myWatchlistsActions.CREATE_MY_WATCHLIST_FAILED
      ),
      takeUntil(this._unsubscribe),
      take(1)
    ).subscribe((action: myWatchlistsActions.CreateMyWatchlistSucceededAction | myWatchlistsActions.CreateMyWatchlistFailedAction) => {
      this.closeCopyWatchlistDialog();

      if (action.type === myWatchlistsActions.CREATE_MY_WATCHLIST_SUCCEEDED) {
        this._store.dispatch(new routerActions.GoAction({ path: ['myWatchlists', action.payload.watchlist.uuid] }));
      }
    });
  }
}
